import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { PriceRowPreview } from "@reina/shared";
import {
  CodeChip,
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import { useRateCards, useSuppliers } from "../../api/catalogue";
import {
  useAmendPrice,
  usePriceList,
  usePriceListRows,
  usePublishPriceList,
} from "../../api/priceLists";
import {
  errorMessage,
  formatDate,
  formatInt,
  formatMoney,
  formatPct,
} from "../../lib/format";
import { numberOrUndefined, textOrNull } from "../../lib/form";
import { Pager } from "../../lib/Pager";
import { priceListStatusLabel, priceListStatusTone } from "../../lib/tones";

const PAGE_SIZE = 50;

export function PriceListDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const detail = usePriceList(id);
  const isParsing = detail.data?.status === "parsing";
  const [offset, setOffset] = useState(0);
  const rows = usePriceListRows(id, { limit: PAGE_SIZE, offset }, isParsing);
  const suppliers = useSuppliers();
  const rateCards = useRateCards();
  const publish = usePublishPriceList();

  const supplier = suppliers.data?.items.find((s) => s.id === detail.data?.supplierId);
  const rateCardById = useMemo(
    () => new Map((rateCards.data?.items ?? []).map((r) => [r.id, r])),
    [rateCards.data]
  );

  const [amend, setAmend] = useState<PriceRowPreview | null>(null);

  if (detail.isPending) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState title="Loading price list…" />
        </PanelBody>
      </Panel>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState
            title="Could not load price list"
            hint={detail.error ? errorMessage(detail.error) : undefined}
            action={<Button onClick={() => navigate("/price-lists")}>Back to price lists</Button>}
          />
        </PanelBody>
      </Panel>
    );
  }

  const list = detail.data;
  const problemCount = (rows.data?.items ?? []).filter((row) => row.problem).length;

  const onPublish = async () => {
    try {
      const result = await publish.mutateAsync(list.id);
      toast.push({
        message: `Published — ${result.inserted} inserted, ${result.closed} closed, ${result.unchanged} unchanged`,
        tone: "ok",
      });
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <div className="stack">
      <div className="breadcrumb">
        <button type="button" onClick={() => navigate("/price-lists")}>
          Price lists
        </button>
        <span>/</span>
        <span className="mono">
          {supplier?.name ?? "Supplier"} v{list.version}
        </span>
      </div>

      <Panel>
        <PanelHead
          title={
            <span className="result-cell">
              {supplier && <SupplierAvatar name={supplier.name} logo={supplier.logo} />}
              {supplier?.name ?? "Price list"} — v{list.version}
            </span>
          }
          sub={`${list.sourceType}${list.fileName ? ` · ${list.fileName}` : ""} · received ${formatDate(list.createdAt)}`}
          actions={
            <>
              <Pill tone={priceListStatusTone[list.status]}>{priceListStatusLabel[list.status]}</Pill>
              {list.status === "parsed" && (
                <Button variant="primary" onClick={() => void onPublish()} disabled={publish.isPending}>
                  {publish.isPending ? "Publishing…" : "Publish"}
                </Button>
              )}
            </>
          }
        />
        <PanelBody className="stack">
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Rows staged</span>
              <strong className="mono">{formatInt(list.rowCount)}</strong>
            </div>
            <div className="info-item">
              <span className="label">Effective date</span>
              <strong className="mono">{formatDate(list.effectiveDate)}</strong>
            </div>
            <div className="info-item">
              <span className="label">Rate card</span>
              <strong className="mono">
                {list.rateCardId
                  ? rateCardById.get(list.rateCardId)?.termsCode ?? list.rateCardId.slice(0, 8)
                  : "Per sheet / derived"}
              </strong>
            </div>
            <div className="info-item">
              <span className="label">Source</span>
              <strong className="mono">{list.sourceType}</strong>
            </div>
          </div>
          {list.parseError && (
            <div className="form-note">
              <strong>Parse failed:</strong> {list.parseError}
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead
          title="Parse summary"
          sub={isParsing ? "parsing in the background — updates live" : "per-sheet breakdown"}
        />
        {list.sheets.length === 0 ? (
          <PanelBody>
            <EmptyState
              title={isParsing ? "Parsing…" : "No sheets parsed"}
              hint={isParsing ? "The parse summary appears when the background job finishes." : undefined}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Rows</th>
                <th>Parsed</th>
                <th>Skipped</th>
                <th>Terms code</th>
                <th>Rate card</th>
                <th>Warnings</th>
              </tr>
            </thead>
            <tbody>
              {list.sheets.map((sheet) => (
                <tr key={sheet.sheetName}>
                  <td>
                    <strong>{sheet.sheetName}</strong>
                  </td>
                  <td className="mono">{formatInt(sheet.rowCount)}</td>
                  <td className="mono">{formatInt(sheet.parsedCount)}</td>
                  <td className="mono">{formatInt(sheet.skippedCount)}</td>
                  <td className="mono">{sheet.termsCode ?? "—"}</td>
                  <td className="mono">
                    {sheet.rateCardId
                      ? rateCardById.get(sheet.rateCardId)?.termsCode ?? sheet.rateCardId.slice(0, 8)
                      : "—"}
                  </td>
                  <td>
                    {sheet.warnings.length === 0 ? (
                      <Pill tone="ok">Clean</Pill>
                    ) : (
                      <span className="sub" style={{ marginTop: 0 }}>
                        {sheet.warnings.join("; ")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      <Panel>
        <PanelHead
          title="Staged rows"
          sub={
            rows.data
              ? `${rows.data.total} row${rows.data.total === 1 ? "" : "s"}${
                  problemCount > 0 ? ` · ${problemCount} problem row${problemCount === 1 ? "" : "s"} on this page` : ""
                }`
              : "parsed rows preview"
          }
        />
        {rows.isPending ? (
          <PanelBody>
            <EmptyState title="Loading rows…" />
          </PanelBody>
        ) : rows.isError ? (
          <PanelBody>
            <EmptyState title="Could not load rows" hint={errorMessage(rows.error)} />
          </PanelBody>
        ) : (rows.data.items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title={isParsing ? "Parsing…" : "No rows staged"}
              hint={isParsing ? "Rows stream in while the parse runs." : "Nothing was parsed from this ingest."}
            />
          </PanelBody>
        ) : (
          <>
            <TableWrap className="comparison-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Sheet</th>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>List</th>
                  <th>Disc</th>
                  <th>Nett</th>
                  <th>UOM</th>
                  <th>Terms</th>
                  <th>Product</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.items.map((row) => (
                  <tr key={row.id} className={row.problem ? "variance-row" : undefined}>
                    <td className="mono">{row.rowNo}</td>
                    <td className="mono">{row.sheetName ?? "—"}</td>
                    <td>
                      <span className="code-stack">
                        <CodeChip code={row.supplierSku} title="Supplier code" />
                        {row.manPartNumber && (
                          <CodeChip code={row.manPartNumber} dim title="Manufacturer part no." />
                        )}
                      </span>
                    </td>
                    <td>
                      {row.description}
                      {row.problem && (
                        <span className="sub">
                          <Pill tone="danger">{row.problem}</Pill>
                        </span>
                      )}
                    </td>
                    <td className="money">{formatMoney(row.listPrice)}</td>
                    <td className="mono">{formatPct(row.discountPct)}</td>
                    <td className="money">{formatMoney(row.nettPrice)}</td>
                    <td className="mono">
                      {[row.uom, row.packQty != null ? `×${row.packQty}` : null]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </td>
                    <td className="mono">{row.termsCode ?? "—"}</td>
                    <td>
                      {row.productId ? (
                        <span className="sub" style={{ marginTop: 0 }}>
                          {[row.brandName, row.canonicalType, row.domain].filter(Boolean).join(" · ") ||
                            row.productId.slice(0, 8)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {row.priceId ? (
                        <span className="result-cell">
                          <span className="mono" title={row.priceId}>
                            {row.priceId.slice(0, 8)}
                          </span>
                          <Button mini onClick={() => setAmend(row)}>
                            Amend
                          </Button>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <Pager total={rows.data.total} limit={PAGE_SIZE} offset={offset} onOffset={setOffset} />
          </>
        ))}
      </Panel>

      {amend && amend.priceId && (
        <AmendModal key={amend.id} row={amend} priceId={amend.priceId} onClose={() => setAmend(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Amend modal — closes the current SCD2 row, inserts a manual_amend row
// ---------------------------------------------------------------------------

function AmendModal({
  row,
  priceId,
  onClose,
}: {
  row: PriceRowPreview;
  priceId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const amend = useAmendPrice();
  const [nett, setNett] = useState(row.nettPrice != null ? String(row.nettPrice) : "");
  const [listPrice, setListPrice] = useState(row.listPrice != null ? String(row.listPrice) : "");
  const [discount, setDiscount] = useState(row.discountPct != null ? String(row.discountPct) : "");
  const [reason, setReason] = useState("");

  const submit = async () => {
    const input = {
      nettPrice: numberOrUndefined(nett),
      listPrice: numberOrUndefined(listPrice),
      discountPct: numberOrUndefined(discount),
      reason: textOrNull(reason) ?? undefined,
    };
    if (input.nettPrice === undefined && input.listPrice === undefined && input.discountPct === undefined) {
      toast.push({ message: "Change at least one price field", tone: "warn" });
      return;
    }
    try {
      await amend.mutateAsync({ priceId, input });
      toast.push({ message: "Price amended — new SCD2 row inserted", tone: "ok" });
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  };

  return (
    <Modal
      open
      title="Amend price"
      sub={row.description}
      onClose={onClose}
      footer={
        <>
          <span className="sub">The current row is closed; history is kept.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={amend.isPending}>
              {amend.isPending ? "Amending…" : "Amend price"}
            </Button>
          </div>
        </>
      }
    >
      <FieldGrid columns={3}>
        <Field label="Nett price £">
          <input
            className="field"
            type="number"
            step="0.0001"
            min={0}
            autoFocus
            value={nett}
            onChange={(event) => setNett(event.target.value)}
          />
        </Field>
        <Field label="List price £">
          <input
            className="field"
            type="number"
            step="0.0001"
            min={0}
            value={listPrice}
            onChange={(event) => setListPrice(event.target.value)}
          />
        </Field>
        <Field label="Discount %">
          <input
            className="field"
            type="number"
            step="0.01"
            min={0}
            max={100}
            value={discount}
            onChange={(event) => setDiscount(event.target.value)}
          />
        </Field>
      </FieldGrid>
      <Field label="Reason" hint="Recorded in the audit trail.">
        <input
          className="field"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. negotiated price on the phone"
        />
      </Field>
    </Modal>
  );
}
